import { useSyncExternalStore } from 'react';
import {
  initialLineTallySnapshot, subscribeInitialLineTally, type InitialLineOutcomeName,
} from '@/stores/initialLineTally';
import { INITIAL_LINE_WAIT_MS, initialLineVolumeSetting } from '@/views/SpreadsTotals/initialLineConfig';

const WAIT = `${INITIAL_LINE_WAIT_MS / 1000} s`;
// #1071 — one row per outcome the count can hold
// (`stores/initialLineTally.ts`), in display order. `satisfies` makes the
// compiler name any outcome this list misses or no longer exists.
const LABELS = {
  'volume:moved': 'Busiest line, not the 50¢ line',
  'volume:at-50c': 'Busiest line, also the 50¢ line',
  'fallback:none-traded': '50¢ line: nothing traded',
  'fallback:busiest-unpriced': '50¢ line: the busiest line has no price',
  'fallback:no-eligible': '50¢ line: no line could be checked (suspended or unmapped)',
  'limit:edge': `50¢ line: volume incomplete at ${WAIT} (edge feed)`,
  'limit:core': `50¢ line: volume incomplete at ${WAIT} (core feed)`,
  'limit:firehose': `50¢ line: volume incomplete at ${WAIT} (OG feed)`,
  'limit:unknown': `50¢ line: volume incomplete at ${WAIT} (feed not named)`,
  touched: 'Trader chose a line first',
} satisfies Record<InitialLineOutcomeName, string>;
const MODES = ['active', 'shadow'] as const;

/** Passive observer: shows the count this tab's board and Game Detail kept.
 *  Opening Health does not open a board, and reads the switch without
 *  changing it. */
export function InitialLineHealth() {
  const { counts, reused } = useSyncExternalStore(subscribeInitialLineTally, initialLineTallySnapshot);
  const total = (mode: string) => Object.entries(counts)
    .filter(([name]) => name.startsWith(`${mode} `)).reduce((sum, [, n]) => sum + n, 0);
  const empty = Object.keys(counts).length === 0 && reused === 0;
  return (
    <section className="border-border min-w-0 rounded-lg border p-4" aria-label="Initial spread/total line count">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">Initial spread/total line</h2>
        <span className="text-xs">Busiest-line rule for the next board or Game Detail visit: {initialLineVolumeSetting() ? 'On' : 'Off'}</span>
      </div>
      <p className="text-muted-foreground mt-1 text-xs">
        This tab only. The count does not change what the board shows. Counts last until sign-out,
        account/organization/role change, or page reload. Opening Health does not open a board.
      </p>
      <p className="text-muted-foreground mt-1 text-xs">
        Each spread or total row is counted once, on the board or in Game Detail, when its first line is settled (in a
        very long session the oldest rows can be counted again); rows still waiting for prices or volume are not counted.
        Game Detail counts only when switched on. &quot;Busiest&quot; means the most traded of
        the three lines around 50¢. &quot;Switched off&quot; counts what the busiest-line rule would have chosen, or
        that a trader chose first, while the board kept the 50¢ line.
      </p>
      {empty ? <p className="text-muted-foreground mt-2 text-xs">No rows counted in this tab yet.</p> : <>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-muted-foreground border-border border-b">
              <tr>
                <th className="py-1.5 pr-3 font-medium">How the first line was chosen</th>
                <th className="px-3 text-right font-medium">Switched on</th>
                <th className="px-3 text-right font-medium">Switched off (would have)</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(LABELS).map(([outcome, label]) => (
                <tr key={outcome} className="border-border/50 border-b">
                  <td className="py-1.5 pr-3">{label}</td>
                  {MODES.map((mode) => (
                    <td key={mode} className="px-3 text-right font-mono tabular-nums">{counts[`${mode} ${outcome}`] ?? 0}</td>
                  ))}
                </tr>
              ))}
              <tr className="font-semibold">
                <td className="py-1.5 pr-3">Rows counted</td>
                {MODES.map((mode) => (
                  <td key={mode} className="px-3 text-right font-mono tabular-nums">{total(mode)}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs">Times the board reused a saved first line (switched on): {reused}</p>
      </>}
    </section>
  );
}
